import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  CardActions,
  InputBase,
  Paper,
  Divider,
} from '@material-ui/core';
import { Link } from '@backstage/core-components';
import SearchIcon from '@material-ui/icons/Search';
import DataUsageIcon from '@material-ui/icons/Storage';
import ApiIcon from '@material-ui/icons/Settings';
import CodeIcon from '@material-ui/icons/Code';
import EventIcon from '@material-ui/icons/Event';
import HealthAndSafetyIcon from '@material-ui/icons/LocalHospital';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import CategoryIcon from '@material-ui/icons/Category';
import BuildIcon from '@material-ui/icons/Build';
import { BrandStripe } from '../../theme/BrandStripe';
import { zambianColors, heroGradient } from '../../theme/zambianTheme';
import { pluginCatalog } from './pluginCatalog';
import { usePlatformStats } from './usePlatformStats';
import { RecentActivityFeed } from './RecentActivityFeed';

const useStyles = makeStyles(theme => ({
  page: {
    backgroundColor: theme.palette.background.default,
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 4),
    backgroundColor: '#FFFFFF',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  brandMark: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    color: zambianColors.ink,
    fontWeight: 700,
    fontSize: '1.1rem',
  },
  brandIcon: {
    color: zambianColors.greenDark,
    fontSize: '2rem',
  },
  hero: {
    minHeight: '64vh',
    display: 'flex',
    alignItems: 'center',
    background: heroGradient,
    color: '#FFFFFF',
    position: 'relative',
    overflow: 'hidden',
    padding: theme.spacing(8, 0),
  },
  heroPattern: {
    position: 'absolute',
    inset: 0,
    opacity: 0.08,
    backgroundImage: 'radial-gradient(circle, #FFFFFF 1px, transparent 1px)',
    backgroundSize: '32px 32px',
  },
  heroAccent: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    width: 8,
    background: zambianColors.orange,
  },
  heroContent: {
    position: 'relative',
    zIndex: 1,
  },
  heroHeadline: {
    fontWeight: 800,
    fontSize: 'clamp(2rem, 5vw, 3.75rem)',
    lineHeight: 1.1,
    marginBottom: theme.spacing(2),
  },
  heroSub: {
    fontWeight: 300,
    fontSize: 'clamp(1rem, 2vw, 1.4rem)',
    maxWidth: 720,
    marginBottom: theme.spacing(4),
    opacity: 0.92,
  },
  cta: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  ctaPrimary: {
    backgroundColor: zambianColors.orange,
    color: '#000000',
    '&:hover': { backgroundColor: '#FFA040' },
  },
  ctaOutline: {
    color: '#FFFFFF',
    borderColor: '#FFFFFF',
    '&:hover': {
      borderColor: zambianColors.orange,
      color: zambianColors.orange,
    },
  },
  searchPaper: {
    marginTop: theme.spacing(4),
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 1, 0.5, 2),
    maxWidth: 640,
    borderRadius: 999,
    backgroundColor: '#FFFFFF',
    boxShadow: '0 12px 30px rgba(0,0,0,0.25)',
  },
  searchIcon: {
    color: zambianColors.greenDark,
    marginRight: theme.spacing(1),
  },
  searchInput: {
    flex: 1,
    color: zambianColors.ink,
    fontSize: '1rem',
  },
  searchSubmit: {
    borderRadius: 999,
    padding: theme.spacing(1, 3),
    backgroundColor: zambianColors.greenDark,
    color: '#FFFFFF',
    '&:hover': { backgroundColor: zambianColors.greenDeep },
  },
  section: {
    padding: theme.spacing(8, 0),
  },
  sectionAlt: {
    backgroundColor: zambianColors.surfaceAlt,
  },
  sectionHeading: {
    fontWeight: 700,
  },
  accentRule: {
    width: 56,
    height: 4,
    backgroundColor: zambianColors.orange,
    border: 0,
    margin: '8px 0 24px',
  },
  statCard: {
    textAlign: 'center',
    padding: theme.spacing(3),
    height: '100%',
    borderTop: `4px solid ${zambianColors.orange}`,
  },
  statValue: {
    fontSize: '2.75rem',
    fontWeight: 800,
    color: zambianColors.greenDark,
    lineHeight: 1,
  },
  statLabel: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    fontSize: '0.8rem',
  },
  categoryCard: {
    height: '100%',
    transition: 'transform 0.2s, box-shadow 0.2s',
    borderTop: `3px solid ${zambianColors.greenDark}`,
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[8],
    },
  },
  categoryIcon: {
    fontSize: '3rem',
    color: zambianColors.greenDark,
    marginBottom: theme.spacing(1),
  },
  pluginCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[6],
    },
  },
  pluginIcon: {
    color: zambianColors.orangeDark,
    fontSize: '2.25rem',
    marginBottom: theme.spacing(1),
  },
  pluginTitle: {
    fontWeight: 700,
  },
  step: {
    textAlign: 'center',
    padding: theme.spacing(3),
    position: 'relative',
  },
  stepNumber: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 48,
    height: 48,
    borderRadius: '50%',
    backgroundColor: zambianColors.greenDark,
    color: '#FFFFFF',
    fontWeight: 700,
    fontSize: '1.25rem',
    marginBottom: theme.spacing(2),
  },
  missionPanel: {
    background: heroGradient,
    color: '#FFFFFF',
    padding: theme.spacing(6),
    borderRadius: 8,
  },
  footer: {
    padding: theme.spacing(6, 0, 0),
    backgroundColor: zambianColors.surfaceDark,
    color: '#E6EFE2',
  },
  footerLink: {
    display: 'block',
    color: '#E6EFE2',
    padding: theme.spacing(0.5, 0),
    textDecoration: 'none',
    '&:hover': { color: zambianColors.orange },
  },
  footerBottom: {
    padding: theme.spacing(3, 0),
    marginTop: theme.spacing(4),
    fontSize: '0.85rem',
    opacity: 0.85,
  },
}));

const categories: Array<{
  title: string;
  description: string;
  icon: typeof DataUsageIcon;
  to: string;
  cta: string;
}> = [
  {
    title: 'Open Data',
    description:
      'Anonymised public-health datasets for research, analytics and surveillance — compliant with privacy regulations.',
    icon: DataUsageIcon,
    to: '/openhmis',
    cta: 'Browse datasets',
  },
  {
    title: 'APIs',
    description:
      'RESTful and FHIR-compliant endpoints for integrating health information systems across the country.',
    icon: ApiIcon,
    to: '/api-docs',
    cta: 'View API docs',
  },
  {
    title: 'Open Source Code',
    description:
      'Community-driven health technology projects, tools and libraries powering Zambia’s digital health ecosystem.',
    icon: CodeIcon,
    to: '/catalog',
    cta: 'Explore projects',
  },
  {
    title: 'Events & Training',
    description:
      'Conferences, workshops, webinars and hackathons focused on health informatics and public-health technology.',
    icon: EventIcon,
    to: '/openhmis',
    cta: 'View calendar',
  },
];

const onboardingSteps = [
  {
    title: 'Sign in with GitHub',
    description:
      'Use your GitHub account to access the developer platform and contribute to projects.',
    icon: LockOpenIcon,
  },
  {
    title: 'Browse the catalog',
    description:
      'Discover services, APIs, datasets and templates owned by Ministry of Health teams.',
    icon: CategoryIcon,
  },
  {
    title: 'Build with templates',
    description:
      'Bootstrap a new service, dataset or dashboard from a curated software template.',
    icon: BuildIcon,
  },
];

const StatCard = ({
  value,
  label,
  loading,
}: {
  value: number;
  label: string;
  loading: boolean;
}) => {
  const classes = useStyles();
  return (
    <Card className={classes.statCard} elevation={2}>
      <Typography className={classes.statValue}>
        {loading ? '—' : value.toLocaleString()}
      </Typography>
      <Typography className={classes.statLabel}>{label}</Typography>
    </Card>
  );
};

export const PublicHomePage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const stats = usePlatformStats();
  const [query, setQuery] = useState('');

  const onSubmitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    navigate(q ? `/search?query=${encodeURIComponent(q)}` : '/search');
  };

  return (
    <Box className={classes.page}>
      <BrandStripe />

      {/* Top bar */}
      <Box className={classes.topBar}>
        <Box className={classes.brandMark}>
          <HealthAndSafetyIcon className={classes.brandIcon} />
          <span>Open HMIS · Ministry of Health</span>
        </Box>
        <Box>
          <Link to="/catalog" style={{ marginRight: 16 }}>
            Catalog
          </Link>
          <Link to="/docs" style={{ marginRight: 16 }}>
            Docs
          </Link>
          <Link to="/settings">Sign in</Link>
        </Box>
      </Box>

      {/* Hero */}
      <Box className={classes.hero}>
        <Box className={classes.heroPattern} />
        <Box className={classes.heroAccent} />
        <Container maxWidth="lg" className={classes.heroContent}>
          <Typography variant="h1" className={classes.heroHeadline}>
            Open Health. Open Data. Open Zambia.
          </Typography>
          <Typography className={classes.heroSub}>
            The Ministry of Health’s developer platform — discover health data,
            APIs, open-source tools and the people behind Zambia’s digital
            health systems.
          </Typography>

          <Box>
            <Link to="/openhmis" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                size="large"
                className={`${classes.cta} ${classes.ctaPrimary}`}
              >
                Explore Open HMIS
              </Button>
            </Link>
            <Link to="/catalog" style={{ textDecoration: 'none' }}>
              <Button
                variant="outlined"
                size="large"
                className={`${classes.cta} ${classes.ctaOutline}`}
              >
                Browse the catalog
              </Button>
            </Link>
          </Box>

          {/* Quick search */}
          <Paper
            component="form"
            elevation={6}
            className={classes.searchPaper}
            onSubmit={onSubmitSearch}
            role="search"
            aria-label="Search the platform"
          >
            <SearchIcon className={classes.searchIcon} />
            <InputBase
              className={classes.searchInput}
              placeholder="Search datasets, services, docs…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              inputProps={{ 'aria-label': 'Search the platform' }}
            />
            <Button type="submit" className={classes.searchSubmit}>
              Search
            </Button>
          </Paper>
        </Container>
      </Box>

      {/* Stats */}
      <Container maxWidth="lg" className={classes.section}>
        <Typography
          variant="h3"
          align="center"
          className={classes.sectionHeading}
        >
          The platform at a glance
        </Typography>
        <Box display="flex" justifyContent="center">
          <hr className={classes.accentRule} />
        </Box>
        <Grid container spacing={4}>
          <Grid item xs={6} md={3}>
            <StatCard
              value={stats.components}
              label="Components"
              loading={stats.loading}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard value={stats.apis} label="APIs" loading={stats.loading} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard
              value={stats.resources}
              label="Resources"
              loading={stats.loading}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard
              value={stats.templates}
              label="Templates"
              loading={stats.loading}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Categories */}
      <Box className={`${classes.section} ${classes.sectionAlt}`}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            className={classes.sectionHeading}
          >
            Explore by category
          </Typography>
          <Box display="flex" justifyContent="center">
            <hr className={classes.accentRule} />
          </Box>
          <Grid container spacing={4}>
            {categories.map(cat => {
              const Icon = cat.icon;
              return (
                <Grid item xs={12} sm={6} md={3} key={cat.title}>
                  <Card className={classes.categoryCard}>
                    <CardContent>
                      <Icon className={classes.categoryIcon} />
                      <Typography variant="h5" gutterBottom>
                        {cat.title}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {cat.description}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Link to={cat.to} style={{ textDecoration: 'none' }}>
                        <Button color="primary">{cat.cta} →</Button>
                      </Link>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* Featured plugins */}
      <Container maxWidth="lg" className={classes.section}>
        <Typography
          variant="h3"
          align="center"
          className={classes.sectionHeading}
        >
          Tools on the platform
        </Typography>
        <Box display="flex" justifyContent="center">
          <hr className={classes.accentRule} />
        </Box>
        <Grid container spacing={3}>
          {pluginCatalog.map(p => {
            const Icon = p.icon;
            return (
              <Grid item xs={12} sm={6} md={4} key={p.id}>
                <Card className={classes.pluginCard}>
                  <CardContent style={{ flex: 1 }}>
                    <Icon className={classes.pluginIcon} />
                    <Typography variant="h6" className={classes.pluginTitle}>
                      {p.title}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {p.tagline}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Link to={p.route} style={{ textDecoration: 'none' }}>
                      <Button color="primary" size="small">
                        Learn more →
                      </Button>
                    </Link>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Container>

      {/* Getting started */}
      <Box className={`${classes.section} ${classes.sectionAlt}`}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            className={classes.sectionHeading}
          >
            Getting started
          </Typography>
          <Box display="flex" justifyContent="center">
            <hr className={classes.accentRule} />
          </Box>
          <Grid container spacing={4}>
            {onboardingSteps.map((step, idx) => {
              const Icon = step.icon;
              return (
                <Grid item xs={12} md={4} key={step.title}>
                  <Box className={classes.step}>
                    <Box className={classes.stepNumber}>{idx + 1}</Box>
                    <Icon
                      style={{
                        fontSize: '2rem',
                        color: zambianColors.orangeDark,
                      }}
                    />
                    <Typography variant="h6" gutterBottom>
                      {step.title}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {step.description}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* Recent activity */}
      <Container maxWidth="md" className={classes.section}>
        <Typography
          variant="h4"
          align="center"
          className={classes.sectionHeading}
        >
          Recent catalog activity
        </Typography>
        <Box display="flex" justifyContent="center">
          <hr className={classes.accentRule} />
        </Box>
        <RecentActivityFeed limit={5} hideWhenEmpty />
      </Container>

      {/* Mission */}
      <Container maxWidth="lg" className={classes.section}>
        <Box className={classes.missionPanel}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h3" gutterBottom>
                Our mission
              </Typography>
              <Typography variant="body1" paragraph>
                Open HMIS promotes transparency, collaboration and innovation in
                Zambia’s public-health system by making digital health resources
                freely available to researchers, developers and health
                professionals.
              </Typography>
              <Typography variant="body1">
                We believe open access to health data and technology accelerates
                discovery, improves outcomes and strengthens public-health
                infrastructure for every Zambian.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} style={{ textAlign: 'center' }}>
              <HealthAndSafetyIcon
                style={{ fontSize: '8rem', color: zambianColors.orange }}
              />
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* Footer */}
      <Box className={classes.footer}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                About Open HMIS
              </Typography>
              <Typography variant="body2">
                A collaborative Ministry of Health platform for sharing public
                health data, APIs and open-source tools to advance health across
                Zambia.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                Tools
              </Typography>
              {pluginCatalog.slice(0, 6).map(p => (
                <Link key={p.id} to={p.route} className={classes.footerLink}>
                  {p.title}
                </Link>
              ))}
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                Resources
              </Typography>
              <Link to="/docs" className={classes.footerLink}>
                Documentation
              </Link>
              <Link to="/api-docs" className={classes.footerLink}>
                API Catalog
              </Link>
              <Link to="/tech-radar" className={classes.footerLink}>
                Tech Radar
              </Link>
              <Link to="/catalog" className={classes.footerLink}>
                Service Catalog
              </Link>
            </Grid>
          </Grid>
          <Divider
            style={{
              marginTop: 32,
              backgroundColor: 'rgba(255,255,255,0.1)',
            }}
          />
          <Box className={classes.footerBottom}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="caption">
                  © {new Date().getFullYear()} Ministry of Health, Republic of
                  Zambia. Open source under Apache-2.0. Only non-sensitive,
                  publicly available health data is hosted on this platform.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4} style={{ textAlign: 'right' }}>
                <Typography variant="caption">
                  Open Health · Open Data · Open Zambia
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Container>
        <BrandStripe />
      </Box>
    </Box>
  );
};
